import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import './styles.css';

// Dev safety net: kill any leftover service worker on first load. Vite dev
// doesn't register one (devOptions.enabled: false in vite.config.ts), but
// a SW from a prior `pnpm build` + serve-locally session can stay registered
// for the localhost origin and intercept fetches that should hit vite HMR.
// No-op in production builds.
if (import.meta.env.DEV && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  }).catch(() => { /* ignore */ });
}

// Lightweight error surfacing until a real reporter (Sentry/Bugsnag) is
// wired via env. Console is better than nothing and survives the SW cache
// layer; swap the body of these handlers for Sentry.captureException later.
if (import.meta.env.PROD) {
  window.addEventListener('error', (e) => {
    console.error('[unhandled]', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    console.error('[unhandledrejection]', e.reason);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 60_000 },
  },
});

const root = createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
